import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BorderMagicButton from '../components/ui/BorderMagicButton';

const screens = [
    {
        subtitle: 'Real-Time Options Flow',
        description:
            'See, in real-time, options trades being taken by institutional traders, hedge funds and smart money.',
        imageUrl:
            'https://firebasestorage.googleapis.com/v0/b/liteflow-40a37.appspot.com/o/dash_screen.png?alt=media&token=580a3698-d97e-4321-b72e-6846db40b638',
    },
    {
        subtitle: 'Super Simple',
        description:
            'Our platform makes it easier than ever to filter the flow with handy quick filters and robust custom filters.',
        imageUrl:
            'https://firebasestorage.googleapis.com/v0/b/liteflow-40a37.appspot.com/o/filter_screen.png?alt=media&token=afa749f9-3bd5-4ac8-9258-50ee8bbcde24',
    },
    {
        subtitle: 'Built-in Alerts',
        description:
            'Just like charts, options flow has indicators too. Our algorithms are always scanning for flow that matches our criteria for high conviction trades, and will appear in your alerts tab.',
        imageUrl:
            'https://firebasestorage.googleapis.com/v0/b/liteflow-40a37.appspot.com/o/alerts_screen.png?alt=media&token=53f2c626-27a0-4648-a0ba-4807ce4d8868',
    },
    {
        subtitle: 'Educational Content',
        // Changed to JSX so we can embed the link
        description: (
            <>
                We have{' '}
                <a
                    href="https://www.youtube.com/watch?v=z5pzhXV79sE&list=PL4ONAItqu1IqbtzL9xnGk3C5ju2T542zv" // <-- replace with your channel URL
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                >
                    YouTube videos
                </a>
                , blogs and a Discord community ready to help you learn how to trade options flow. Feel free to ask any questions you may have in the Discord.
            </>
        ),
        imageUrl:
            'https://firebasestorage.googleapis.com/v0/b/liteflow-40a37.appspot.com/o/discord_screen.png?alt=media&token=347069b2-d2b8-442a-8f14-c8f826dbea2b',
    },
];

export default function OnboardingCarousel() {
    const navigate = useNavigate();
    const [index, setIndex] = useState(0);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [titleFaded, setTitleFaded] = useState(false);
    const [carouselVisible, setCarouselVisible] = useState(false);

    // 1) Preload images
    useEffect(() => {
        let loaded = 0;
        screens.forEach(({ imageUrl }) => {
            const img = new Image();
            img.src = imageUrl;
            img.onload = () => {
                if (++loaded === screens.length) {
                    setImagesLoaded(true);
                }
            };
        });
    }, []);

    // 2) Once images are ready, kick off the fade timeline
    useEffect(() => {
        if (!imagesLoaded) return;

        const fadeOutTitle = setTimeout(() => setTitleFaded(true), 1000);
        const showCarousel = setTimeout(() => setCarouselVisible(true), 1500);

        return () => {
            clearTimeout(fadeOutTitle);
            clearTimeout(showCarousel);
        };
    }, [imagesLoaded]);

    const prev = () => setIndex((i) => (i - 1 + screens.length) % screens.length);
    const next = () => setIndex((i) => (i + 1) % screens.length);

    if (!imagesLoaded) {
        return (
            <div className="min-h-screen flex items-center justify-center text-white">
                Please wait…
            </div>
        );
    }

    return (
        <div className="relative min-h-screen bg-black text-white overflow-hidden">
            {/* Stage 1: Centered title */}
            <div
                className={
                    'absolute inset-0 flex items-center justify-center ' +
                    'transition-opacity ' +
                    (titleFaded
                        ? 'opacity-0 pointer-events-none'
                        : 'opacity-100 pointer-events-auto')
                }
                style={{ transitionDuration: '3s' }}
            >
                <h1 className="text-3xl font-bold">
                    Welcome to Bullflow! Here’s what to expect.
                </h1>
            </div>

            {/* Stage 2: Carousel */}
            <div
                className={
                    'min-h-screen flex flex-col items-center justify-start pt-8 ' +
                    'transition-opacity ease-in-out ' +
                    (carouselVisible
                        ? 'opacity-100 pointer-events-auto'
                        : 'opacity-0 pointer-events-none')
                }
                style={{ transitionDuration: '3s' }}
            >
                <div className="relative w-full max-w-3xl overflow-visible group">
                    {/* Image */}
                    <div
                        className="relative w-full mb-6 overflow-hidden rounded-xl aspect-video"
                        style={{
                            WebkitMaskImage:
                                'linear-gradient(to top, transparent 0%, black 50%, black 100%)',
                            maskImage:
                                'linear-gradient(to top, transparent 0%, black 50%, black 100%)',
                        }}
                    >
                        <img
                            src={screens[index].imageUrl}
                            alt={screens[index].subtitle}
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {/* Subtitle */}
                    <p className="text-xl font-semibold mt-2 mb-4 text-center w-full">
                        {screens[index].subtitle}
                    </p>

                    {/* Description with arrows */}
                    <div className="flex items-center justify-center mb-6 px-4">
                        <button
                            onClick={prev}
                            className="p-2 hover:bg-white/20 rounded-full transition"
                        >
                            <ArrowLeft size={24} />
                        </button>

                        <p className="text-gray-300 italic text-center mx-6 text-lg">
                            {screens[index].description}
                        </p>

                        <button
                            onClick={next}
                            className="p-2 hover:bg-white/20 rounded-full transition"
                        >
                            <ArrowRight size={24} />
                        </button>
                    </div>
                </div>

                <BorderMagicButton
                    text="Continue to Dashboard"
                    onClick={() => navigate('/home')}
                />
            </div>
        </div>
    );
}
